import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { orderBy } from 'lodash'
import React, { useEffect, useMemo, useState } from 'react'
import { Avatar, Box, Button, Card, CardActionArea, CardContent, Grid, Tooltip, Typography } from '@mui/material'
import useTitles from 'common/hooks/useTitles'
import { useNavigate } from 'react-router'
import Input from 'common/components/form/Input'
import Select from 'common/components/form/Select'
import { CardTitle } from 'common/components/card/Card'
import { fetchUsers, selectUsers } from 'admin/users/UsersSlice'
import { HABILITATIONS } from 'admin/user/UserHabilitations'
import { darkBlue, mainBlue, mediumGrey, secondaryBlue } from 'app/theme'

const AVATAR_SIZE = 88

const DEFAULT_FILTERS = { search: '', habs: [] }

const ROLE_OPTIONS = HABILITATIONS.map(({ key, label }) => ({ value: key, label }))

const UserCard = ({ user }) => {
    const navigate = useNavigate()

    return (
        <Card
            sx={{
                height: '100%',
                overflow: 'hidden',
                borderRadius: '12px',
                border: '1px solid rgba(22, 24, 50, 0.08)',
                boxShadow: '0 1px 3px rgba(22, 24, 50, 0.08)',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                '&:hover': {
                    transform: 'translateY(-3px)',
                    borderColor: secondaryBlue,
                    boxShadow: '0 8px 20px rgba(22, 24, 50, 0.18)',
                },
            }}
        >
            <CardActionArea
                onClick={() => navigate(`/user/${user.login}`)}
                sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', p: 0 }}
            >
                <Box
                    sx={{
                        width: '100%',
                        height: 56,
                        background: `linear-gradient(135deg, ${darkBlue} 0%, ${mainBlue} 100%)`,
                    }}
                />
                <Avatar
                    src={user.picture}
                    alt={user.login}
                    variant='rounded'
                    sx={{
                        width: AVATAR_SIZE,
                        height: AVATAR_SIZE,
                        mt: `-${AVATAR_SIZE / 2}px`,
                        borderRadius: '18%',
                        border: '4px solid white',
                        boxShadow: '0 2px 8px rgba(22, 24, 50, 0.25)',
                        bgcolor: mainBlue,
                        fontSize: 36,
                        fontWeight: 'bold',
                    }}
                >
                    {user.login?.charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ width: '100%', px: 2, pt: 1.5, pb: 2, textAlign: 'center' }}>
                    <Typography noWrap sx={{ color: darkBlue, fontWeight: 'bold', fontSize: 16 }}>
                        {user.login}
                    </Typography>
                    <Typography noWrap sx={{ color: mediumGrey, fontSize: 12.5 }}>
                        {user.email}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1.5 }}>
                        {HABILITATIONS.map(({ key, label, Icon }) => {
                            const active = !!user.habs?.includes(key)
                            return (
                                <Tooltip key={key} title={active ? label : `${label} : non`}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: 30,
                                            height: 30,
                                            borderRadius: '8px',
                                            color: active ? 'white' : 'rgba(22, 24, 50, 0.2)',
                                            backgroundColor: active ? mainBlue : 'rgba(22, 24, 50, 0.05)',
                                        }}
                                    >
                                        <Icon sx={{ fontSize: 18 }} />
                                    </Box>
                                </Tooltip>
                            )
                        })}
                    </Box>
                </Box>
            </CardActionArea>
        </Card>
    )
}

UserCard.propTypes = {
    user: PropTypes.shape({
        picture: PropTypes.string,
        login: PropTypes.string,
        email: PropTypes.string,
        habs: PropTypes.arrayOf(PropTypes.string),
    }),
}

const FilterUsers = ({ filters = DEFAULT_FILTERS, setFilters = () => {}, total = 0, count = 0 }) => {
    const { search, habs } = filters
    return (
        <Card>
            <CardTitle title='Filtre' color={darkBlue} subTitle={`${count} / ${total}`} subtitleSize='auto' />
            <CardContent>
                <Grid container spacing={1} alignItems='center'>
                    <Grid size={4}>
                        <Input
                            label='Rechercher'
                            value={search}
                            onChange={v => setFilters({ ...filters, search: v })}
                        />
                    </Grid>
                    <Grid size={3}>
                        <Select
                            multiple
                            label='Rôles'
                            value={habs}
                            items={ROLE_OPTIONS}
                            onChange={v => setFilters({ ...filters, habs: v })}
                            undefinedValue
                        />
                    </Grid>
                    <Grid container size='grow' justifyContent='flex-end' alignItems='center'>
                        <Button variant='outlined' onClick={() => setFilters(DEFAULT_FILTERS)}>Réinitialiser</Button>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

FilterUsers.propTypes = {
    filters: PropTypes.shape({
        search: PropTypes.string,
        habs: PropTypes.arrayOf(PropTypes.string),
    }),
    setFilters: PropTypes.func,
    total: PropTypes.number,
    count: PropTypes.number,
}

const UsersApp = () => {
    const dispatch = useDispatch()

    const users = useSelector(selectUsers, shallowEqual)
    const [filters, setFilters] = useState(DEFAULT_FILTERS)

    useEffect(() => {
        dispatch(fetchUsers())
    }, [dispatch])

    useTitles(() => [{
        label: 'Utilisateurs',
        href: '/user',
    }], [])

    const filteredUsers = useMemo(() => {
        const { search, habs } = filters
        const lowerSearch = (search ?? '').trim().toLowerCase()
        const bySearch = users.filter(user => `${user.login} ${user.email}`.toLowerCase().includes(lowerSearch))
        const byHabs = habs?.length ? bySearch.filter(user => habs.some(hab => user.habs?.includes(hab))) : bySearch
        return orderBy(byHabs, user => user.login.toLowerCase())
    }, [users, filters])

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FilterUsers filters={filters} setFilters={setFilters} total={users.length} count={filteredUsers.length} />
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
                    gap: 2,
                    p: 1,
                }}
            >
                {filteredUsers.map(user => (
                    <UserCard key={user.login} user={user} />
                ))}
            </Box>
        </Box>
    )
}

export default UsersApp
